/**
 * Environment Configuration with Runtime Validation
 * Uses Zod for type-safe environment variable parsing
 * Fails fast on startup if required variables are missing
 */

import { z } from 'zod';

// Define the environment schema
// NOTE: The OpenAI API key is no longer on the client.
// It lives server-side in the Cloudflare Pages Function (OPENAI_API_KEY).
const envSchema = z.object({
  // Optional with defaults
  VITE_SENTRY_DSN: z.string().optional().default(''),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).optional().default('development'),
  VITE_APP_VERSION: z.string().optional().default('1.0.0'),
});

// Type inference from schema
export type Env = z.infer<typeof envSchema>;

// Parse and validate environment variables
function validateEnv(): Env {
  const envVars = {
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
    VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
  };

  const result = envSchema.safeParse(envVars);

  if (!result.success) {
    const errors = result.error.flatten().fieldErrors;
    const errorMessages = Object.entries(errors)
      .map(([key, msgs]) => `  ${key}: ${msgs?.join(', ')}`)
      .join('\n');

    console.error(
      `❌ Environment Validation Failed:\n${errorMessages}\n\n` +
        `Please check your .env file. See .env.example for required variables.`
    );

    // In development, show a helpful error
    if (import.meta.env.DEV) {
      console.warn('Missing environment variables. AI features may be limited.');
    }

    // Return defaults
    return envSchema.parse(envVars);
  }

  return result.data;
}

// Export validated environment
export const env = validateEnv();

// AI availability — the API key is now server-side so AI is always enabled.
// The proxy returns 503 if the key is missing.
export const isAIEnabled = (): boolean => true;

// Helper to check which AI provider is available
export const getAIProvider = (): 'openai' | null => 'openai';

// Helper to check environment
export const isDev = () => env.VITE_APP_ENV === 'development';
export const isProd = () => env.VITE_APP_ENV === 'production';
export const isStaging = () => env.VITE_APP_ENV === 'staging';
